import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getFinanceRecords, getFinanceSummary, createFinanceRecord, deleteFinanceRecord,
  getPresupuestos, exportFinance
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, Wallet, TrendingUp, TrendingDown, Users, Trash2, PiggyBank, Download, ExternalLink, Edit, CheckCircle2, Lock, XCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

import { useNotifications } from '../contexts/NotificationContext';

const PRES_STATUS = {
  borrador: { label: 'Borrador', color: 'bg-stone-100 text-stone-700', icon: Edit },
  confirmado: { label: 'Confirmado', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
  cerrado: { label: 'Cerrado', color: 'bg-emerald-100 text-emerald-700', icon: Lock },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const RECORD_TYPES = [
  { value: 'ingreso', label: 'Ingreso', icon: TrendingUp, color: 'text-emerald-600' },
  { value: 'gasto', label: 'Gasto', icon: TrendingDown, color: 'text-red-600' },
  { value: 'aportacion', label: 'Aportación', icon: Users, color: 'text-sky-600' }
];

export default function Finance() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { formatCurrency: ctxFormatCurrency } = useNotifications();
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateRecord, setShowCreateRecord] = useState(false);
  const [recordForm, setRecordForm] = useState({
    record_type: 'ingreso',
    amount: '',
    description: '',
    category: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recordsRes, summaryRes, presRes] = await Promise.all([
        getFinanceRecords(),
        getFinanceSummary(),
        getPresupuestos()
      ]);
      setRecords(recordsRes.data);
      setSummary(summaryRes.data);
      setPresupuestos(presRes.data);
    } catch (error) {
      toast.error('Error al cargar datos financieros');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRecord = async () => {
    if (!recordForm.amount || !recordForm.description) {
      toast.error('Completa los campos requeridos');
      return;
    }
    try {
      await createFinanceRecord({
        ...recordForm,
        amount: parseFloat(recordForm.amount)
      });
      toast.success('Registro creado');
      setShowCreateRecord(false);
      setRecordForm({ record_type: 'ingreso', amount: '', description: '', category: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear registro');
    }
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try {
      await deleteFinanceRecord(id);
      toast.success('Registro eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar registro');
    }
  };

  const formatCurrency = (amount) => {
    try { return ctxFormatCurrency(amount); }
    catch { return `€${(amount || 0).toFixed(2)}`; }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d MMM yyyy", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const canEdit = hasPermission('finanzas', 'editar');
  const canExport = hasPermission('finanzas', 'exportar');

  const handleExport = async () => {
    try {
      const res = await exportFinance();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `finanzas_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toast.success('Exportación descargada');
    } catch { toast.error('Error al exportar'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="finance-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-emerald-900 font-['Outfit']">
            Gestión Financiera
          </h1>
          <p className="text-muted-foreground">
            Control de ingresos, gastos y aportaciones
          </p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <Button variant="outline" onClick={handleExport} data-testid="export-finance-btn">
              <Download className="w-4 h-4 mr-2" /> Exportar CSV
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Balance</p>
                <p className={`text-2xl font-bold font-['Outfit'] ${summary?.balance >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                  {formatCurrency(summary?.balance)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-emerald-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Ingresos</p>
                <p className="text-2xl font-bold text-emerald-600 font-['Outfit']">
                  {formatCurrency(summary?.total_income)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Gastos</p>
                <p className="text-2xl font-bold text-red-600 font-['Outfit']">
                  {formatCurrency(summary?.total_expense)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Aportaciones</p>
                <p className="text-2xl font-bold text-sky-600 font-['Outfit']">
                  {formatCurrency(summary?.total_contribution)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-sky-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="records" className="space-y-4">
        <TabsList>
          <TabsTrigger value="records">Movimientos</TabsTrigger>
          <TabsTrigger value="budgets">Presupuestos</TabsTrigger>
        </TabsList>

        <TabsContent value="records">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Movimientos Financieros</CardTitle>
              {canEdit && (
                <Button onClick={() => setShowCreateRecord(true)} data-testid="create-record-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Registro
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead>Fecha</TableHead>
                      {canEdit && <TableHead className="text-right">Acciones</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {records.map((record) => {
                      const typeInfo = RECORD_TYPES.find(t => t.value === record.record_type);
                      const Icon = typeInfo?.icon || TrendingUp;
                      return (
                        <TableRow key={record.id} className="table-row-hover">
                          <TableCell>
                            <Badge variant="outline" className={`${typeInfo?.color} border-current`}>
                              <Icon className="w-3 h-3 mr-1" />
                              {typeInfo?.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{record.description}</p>
                              {record.category && (
                                <p className="text-sm text-muted-foreground">{record.category}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className={`text-right font-medium ${
                            record.record_type === 'gasto' ? 'text-red-600' : 'text-emerald-600'
                          }`}>
                            {record.record_type === 'gasto' ? '-' : '+'}{formatCurrency(record.amount)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(record.created_at)}
                          </TableCell>
                          {canEdit && (
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteRecord(record.id)}
                                data-testid={`delete-record-${record.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    {records.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                          No hay registros financieros
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="budgets">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <PiggyBank className="w-5 h-5" />
                Presupuestos
              </CardTitle>
              <Button onClick={() => navigate('/erp/presupuestos')} variant="outline" data-testid="go-to-presupuestos-btn">
                <ExternalLink className="w-4 h-4 mr-2" />
                Ir a Presupuestos
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {presupuestos.length > 0 ? (
                  presupuestos.map((p) => {
                    const st = PRES_STATUS[p.estado] || PRES_STATUS.borrador;
                    const StIcon = st.icon;
                    const recaudado = p.total_recaudado || 0;
                    const total = p.total_presupuesto || 0;
                    const progress = total > 0 ? (recaudado / total) * 100 : 0;
                    return (
                      <div
                        key={p.id}
                        className="p-4 rounded-lg bg-stone-50 border hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate('/erp/presupuestos')}
                        data-testid={`finance-budget-${p.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-stone-800">{p.nombre}</h4>
                              <Badge className={`text-xs ${st.color}`}>
                                <StIcon className="w-3 h-3 mr-1" />{st.label}
                              </Badge>
                            </div>
                            {p.descripcion && <p className="text-xs text-stone-500 truncate">{p.descripcion}</p>}
                          </div>
                          <p className="text-lg font-bold text-stone-800 ml-4">{formatCurrency(total)}</p>
                        </div>
                        {p.estado !== 'borrador' && (
                          <>
                            <Progress value={progress} className="h-2 mb-2" />
                            <div className="flex justify-between text-xs text-stone-500">
                              <span>Recaudado: {formatCurrency(recaudado)}</span>
                              <span>Pendiente: {formatCurrency(total - recaudado)}</span>
                              <span>{(p.numero_participantes || 0)} participantes</span>
                            </div>
                          </>
                        )}
                        {p.estado === 'borrador' && (
                          <div className="flex gap-4 mt-1 text-xs text-stone-400">
                            <span>{(p.numero_participantes || p.participantes_ids?.length || 0)} participantes</span>
                            <span>{(p.partidas?.length || 0)} partidas</span>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <PiggyBank className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No hay presupuestos creados</p>
                    <Button variant="link" className="mt-2" onClick={() => navigate('/erp/presupuestos')}>
                      Crear uno en Presupuestos
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Record Dialog */}
      <Dialog open={showCreateRecord} onOpenChange={setShowCreateRecord}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Registro Financiero</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select
                value={recordForm.record_type}
                onValueChange={(value) => setRecordForm({ ...recordForm, record_type: value })}
              >
                <SelectTrigger data-testid="record-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECORD_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Monto (Bs.) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={recordForm.amount}
                onChange={(e) => setRecordForm({ ...recordForm, amount: e.target.value })}
                placeholder="0.00"
                data-testid="record-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción *</Label>
              <Textarea
                value={recordForm.description}
                onChange={(e) => setRecordForm({ ...recordForm, description: e.target.value })}
                placeholder="Descripción del movimiento..."
                rows={2}
                data-testid="record-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Input
                value={recordForm.category}
                onChange={(e) => setRecordForm({ ...recordForm, category: e.target.value })}
                placeholder="Ej: Transporte, Comida, etc."
                data-testid="record-category"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateRecord(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateRecord} data-testid="submit-record-btn">
              Crear Registro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
